/**
 * Addons Service — frontend client for the credits-billed addons.
 *
 * Two addons currently in scope; each follows the same lifecycle:
 *
 *   GET    /addons/<addon>/status     → current state, rate, EIP/quantity
 *   POST   /addons/<addon>/enable     → provision + start meter (body opt)
 *   DELETE /addons/<addon>/disable    → tear down + stop meter
 *
 * After Wave B-2 lands, /billing/info embeds the per-addon shape under
 * `addons.dedicatedEgress` and `addons.warmPool`, so the BillingPage can
 * render the cards from a single response without N+1 fetches. The
 * per-addon endpoints below are kept for ENABLE/DISABLE actions (they
 * touch infra + ledger, so the dedicated handlers stay the source of
 * truth for mutation).
 */
import { accountApi as apiService } from './api';

const Addons = {
  // ── Dedicated Egress ──────────────────────────────────────────────
  async enableDedicatedEgress() {
    return apiService.request('/addons/dedicated-egress/enable', { method: 'POST' });
  },
  async disableDedicatedEgress() {
    return apiService.request('/addons/dedicated-egress/disable', { method: 'DELETE' });
  },
  async getDedicatedEgressStatus() {
    return apiService.request('/addons/dedicated-egress/status');
  },

  // ── Warm Pool ─────────────────────────────────────────────────────
  /**
   * Enable warm pool with `quantity` instances. Per-instance rate is
   * fixed server-side; quantity is the only knob.
   *
   * Backend wires POST /addons/warm-pool/enable (added by Wave B-1) —
   * if it 404s, fall through to the legacy /addons/warm-pool/scale
   * endpoint which also accepts quantity.
   */
  async enableWarmPool(quantity) {
    const q = Number(quantity);
    if (!Number.isInteger(q) || q < 1) {
      throw new Error('quantity must be a positive integer');
    }
    return apiService.request('/addons/warm-pool/enable', {
      method: 'POST',
      body: JSON.stringify({ quantity: q }),
    });
  },
  async disableWarmPool() {
    return apiService.request('/addons/warm-pool/disable', { method: 'DELETE' });
  },
  async getWarmPoolStatus() {
    return apiService.request('/addons/warm-pool/status');
  },
};

export default Addons;
